import React, { useState, useEffect } from 'react';
import { getUsers, register, changeUserPassword, deleteUser } from '../api';

function UserManagement({ user }) {
  const [users, setUsers] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'standard' });
  const [passwordChange, setPasswordChange] = useState({ userId: null, password: '' });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await getUsers();
      setUsers(response.data);
    } catch (err) {
      setError('Failed to load users');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!newUser.username || !newUser.password) {
      setError('Username and password are required');
      return;
    }

    if (newUser.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      await register(newUser.username, newUser.password, newUser.role);
      setMessage(`User "${newUser.username}" created successfully`);
      setNewUser({ username: '', password: '', role: 'standard' });
      setShowAddUser(false);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
    }
  };

  const handleChangePassword = async (userId) => {
    setError('');
    setMessage('');

    if (!passwordChange.password) {
      setError('Password is required');
      return;
    }

    if (passwordChange.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      await changeUserPassword(userId, passwordChange.password);
      setMessage('Password updated successfully');
      setPasswordChange({ userId: null, password: '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update password');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) {
      return;
    }

    setError('');
    setMessage('');

    try {
      await deleteUser(userId);
      setMessage(`User "${username}" deleted successfully`);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  if (user.role !== 'admin') {
    return (
      <div className="container">
        <div className="alert alert-error">
          Only administrators can manage users.
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">User Management</div>

        {message && <div className="alert alert-success">{message}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        <button
          className="btn btn-primary"
          onClick={() => setShowAddUser(!showAddUser)}
          style={{ marginBottom: '20px' }}
        >
          {showAddUser ? 'Cancel' : 'Add New User'}
        </button>

        {showAddUser && (
          <form onSubmit={handleAddUser} style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <div className="form-group">
              <label>Username *</label>
              <input
                type="text"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Password *</label>
              <input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Role *</label>
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              >
                <option value="standard">Standard</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <button type="submit" className="btn btn-success">
              Create User
            </button>
          </form>
        )}

        <table className="table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Role</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.username}</td>
                <td>{u.role}</td>
                <td>{new Date(u.created_at).toLocaleDateString()}</td>
                <td>
                  {passwordChange.userId === u.id ? (
                    <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                      <input
                        type="password"
                        placeholder="New password"
                        value={passwordChange.password}
                        onChange={(e) => setPasswordChange({ ...passwordChange, password: e.target.value })}
                        style={{ width: '150px' }}
                      />
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => handleChangePassword(u.id)}
                      >
                        Save
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setPasswordChange({ userId: null, password: '' })}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setPasswordChange({ userId: u.id, password: '' })}
                      >
                        Change Password
                      </button>
                      {u.id !== user.id && (
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteUser(u.id, u.username)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default UserManagement;
